import * as PropTypes from 'prop-types';
import * as React from 'react';
import { NotificationManager } from 'react-notifications';

interface ITheme {[key: string]: string; }
interface IProps extends React.HTMLAttributes<HTMLElement> {
  json?: any;
  data?: any;
  replacer?: (key: string, value: any) => any | null;
  space?: number | string;
  themeClassName?: string;
  theme?: ITheme;
  silent?: boolean;
  onJSONPrettyError?: (e: Error) => void;
  mainStyle?: string;
  keyStyle?: string;
  stringStyle?: string;
  valueStyle?: string;
  booleanStyle?: string;
  errorStyle?: string;
  fieldName? : string;
  idToScroll? : string;
  fieldIndex? : number;
  fieldKeys? : any;
  markFields?: boolean;
  markedFields?: string[];
}

interface IState {
  markingEnabled: boolean;
}

function getStyleValue(name: string, theme: ITheme, styles: any): string {
  const extra = styles[name + 'Style'] || '';
  const style = theme ? theme[name] || '' : '';
  return extra ? `${extra};${style}` : style;
}

function getStyle(name: string, theme: ITheme, styles: any): string {
  const value = getStyleValue(name, theme, styles);
  return value ? ` style="${value}"` : '';
}

const xssmap: {[key: string]: string} = {
  '"': '&quot;',
  '\'': '&apos;',
  '&': '&amp;',
  '>': '&gt;',
  '<': '&lt',
};

function xss(s: string): string {
  if (!s) {
    return s;
  }

  return s.replace(/<|>|&|"|'/g, (m) => {
    return xssmap[m];
  });
}
let fieldIndexCounter = 88;
let markedFieldCounter = 0;

class JSONPretty extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = { markingEnabled: !!(props.markedFields && props.markedFields.length > 0) };
  }

  public static propTypes = {
    data: PropTypes.any,
    json: PropTypes.any,
    replacer: PropTypes.func,
    silent: PropTypes.bool,
    space: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    theme: PropTypes.object,
    themeClassName: PropTypes.string,
    onJSONPrettyError: PropTypes.func,
    fieldName : PropTypes.string,
    idToScroll : PropTypes.string,
    fieldIndex : PropTypes.number,
    fieldKeys : PropTypes.any,
    markFields: PropTypes.bool,
    markedFields: PropTypes.arrayOf(PropTypes.string),
  };

  public static defaultProps = {
    data: '',
    json: '',
    silent: true,
    space: 2,
    themeClassName: '__json-pretty__',
    markFields: false,
    markedFields: [],
  };

  public render() {
    const {
      json, data, replacer, space, themeClassName, theme, onJSONPrettyError, onError, silent,
      mainStyle,
      keyStyle,
      valueStyle,
      stringStyle,
      booleanStyle,
      errorStyle,
      fieldName,
      idToScroll,
      fieldIndex,
      fieldKeys,
      markFields,
      markedFields,
      ...rest
    } = this.props;

    const { markingEnabled } = this.state;

    const styles = {
      mainStyle,
      keyStyle,
      valueStyle,
      stringStyle,
      booleanStyle,
      errorStyle
    };

    let obj = data || json;

    // See https://facebook.github.io/react/warnings/unknown-prop.html
    if (typeof obj === 'string') {
      try {
        obj = JSON.parse(obj);
      } catch (e) {
        if (!silent) {
          //console.warn(`[react-json-pretty]: ${e.message}`);
        }

        /*if (onJSONPrettyError) {
          onJSONPrettyError(e);
        }

        if (!onJSONPrettyError && onError) {
          onError(e);
          console.warn('JSONPretty#onError is deprecated, please use JSONPretty#onJSONPrettyError instead');
        }*/

        return(
          <div {...rest} dangerouslySetInnerHTML={
            {__html:
              `<pre class="__json-pretty-error__"${getStyle('error', theme!, styles)}>${xss(obj)}</pre>`
            }
          }>
          </div>
        );
      }
    }

    const copyJson = () => {
      navigator.clipboard.writeText(JSON.stringify(json, null, 2)).then(() => {
        NotificationManager.success('JSON copied to clipboard');
      });
    };

    const effectiveMarkFields = markFields! && markingEnabled;

    return (
      <div className="__pretty_parent__" {...rest} style={{height:"100%"}}>
        <div className="__pretty_toolbar__">
          <button onClick={copyJson}>Copy</button>
          {markFields && (
            <button onClick={() => {
              if (!markingEnabled && (!markedFields || markedFields.length === 0)) {
                NotificationManager.success('No country specific fields, you need to worry about.');
                return;
              }
              this.setState({ markingEnabled: !markingEnabled }, () => {
                if (this.state.markingEnabled)
                  document.getElementById('__first_marked_field__')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              });
            }}>
              {markingEnabled ? 'Unmark country specific' : 'Mark country specific'}
            </button>
          )}
        </div>
        <div dangerouslySetInnerHTML={
          {__html:
            `<pre class="${themeClassName}"${getStyle('main', theme!, styles)}>${
              this._pretty(theme!, obj, replacer!, +space!, styles, fieldName, idToScroll, fieldIndex!, fieldKeys, effectiveMarkFields, markedFields!)
            }
            </pre>`
          }
        } />
      </div>
    );
  }

  /* private copyJson(json:any){
    navigator.clipboard.writeText(JSON.stringify(json));
  } */
  // JSON =》 HTML转换器
  private _pretty(theme: ITheme, obj: any, replacer: (k: string, v: any) => any, space: number, styles: any, fieldName:any, idToScroll:any, fieldIndex:number, fieldKeys:any, markFields: boolean, markedFields: string[]) {
    // 逐行匹配，列举："key": "value" | "key": value | "key": [ | "key": { | "key": [],| "Key": {},
    const regLine = /^( *)("[^"]+": )?("[^"]*"|[\w.+-]*)?([,[{]|\[\s*\],?|\{\s*\},?)?$/mg;
    const text = JSON.stringify(obj,  null, isNaN(space) ? 2 : space);

    /* istanbul ignore next */
    if (!text) {
      return text;
    }
    fieldIndexCounter=0;
    markedFieldCounter=0;
    return text.replace(/&/g, '&amp;').replace(/\\"([^,])/g, '\\&quot;$1')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(regLine, this._replace.bind(null, theme, fieldName, idToScroll, fieldIndex, fieldKeys, styles, markFields, markedFields));
  }

  // 格式化函数
  private _replace(theme: ITheme, fieldName:any, idToScroll:any, fieldIndex:number, fieldKeys:any, styles: any, markFields: boolean, markedFields: string[], match: any, ind: string, key: string, val: string, tra: string) {
    let keySpan;

    if(key && key.indexOf('"'+fieldName+'"') > -1 && (fieldIndex === null || fieldIndex===undefined || fieldIndex===fieldIndexCounter)){
        styles.keyStyle = "background: #d1d1d1;";
        keySpan = `<span id="${idToScroll}" class="__json-key__"${getStyle('key', theme, styles)}>`;
    }else if(key && fieldKeys && fieldKeys.indexOf(key.replace(/[:" ]+/g, ''))>= 0){
      styles.keyStyle = "background: #caeaff;";
      keySpan = `<span id="${idToScroll}" class="__json-key__"${getStyle('key', theme, styles)}>`;
    }else{
        styles.keyStyle = null;
        keySpan = `<span class="__json-key__"${getStyle('key', theme, styles)}>`;
    }

    /*if(key && fieldKeys && fieldKeys.indexOf(key.replace(/[:" ]+/g, ''))>= 0){
        styles.keyStyle = "background: #caeaff;";
        keySpan = `<span id="${idToScroll}" class="__json-key__"${getStyle('key', theme, styles)}>`;
    }else{
        styles.keyStyle = null;
        keySpan = `<span class="__json-key__"${getStyle('key', theme, styles)}>`;
    }*/

    if(key && key.indexOf(fieldName) > 0){
      fieldIndexCounter++;
    }

    const spanEnd = '</span>';
    //const keySpan = `<span class="__json-key__"${getStyle('key', theme, styles)}>`;
    const valSpan = `<span class="__json-value__"${getStyle('value', theme, styles)}>`;
    const strSpan = `<span class="__json-string__"${getStyle('string', theme, styles)}>`;
    const booSpan = `<span class="__json-boolean__"${getStyle('boolean', theme, styles)}>`;

    let sps = ind || '';
    if (key) {
      const cleanKey = key.replace(/^"|":\s$/g, '');
      const isMarked = markFields && markedFields && markedFields.includes(cleanKey);
      const isFirst = isMarked && markedFieldCounter++ === 0;
      const keyContent = isMarked
        ? `<span id="${isFirst ? '__first_marked_field__' : ''}" style="background: #ffe082; border-radius: 3px; padding: 0 3px; font-weight: 600;">${cleanKey}</span>`
        : cleanKey;
      sps = sps + '"' + keySpan + keyContent + spanEnd + '": ';
    }

    if (val) {
      if (val === 'true' || val === 'false') {
        sps = sps +  booSpan + val + spanEnd;
      } else {
        sps = sps + (val[0] === '"' ? strSpan : valSpan) + val + spanEnd;
      }
    }

    return sps + (tra || '');
  }
}

export default JSONPretty;
